<template>
  <div class="home-gift-cards-view">
    <account-email-pending-verification v-if="authenticated && emailNeedVerification && !canRequestCard && displayAccountEmailPendingVerificationMessage" />
    <account-card-required v-if="authenticated && canRequestCard && displayAccountCardRequiredMessage" :redirect-link="cardApplyRedirectLink" />
    <div class="home-gift-cards-view-content">
      <h-btn @click="backHome" color="text-primary" text class="back-btn">
        <v-icon>mdi-menu-left</v-icon>
        {{ $t('components.home_gift_cards_view.back_btn') }}
      </h-btn>

      <h1 class="title-header">{{ $t('components.home_gift_cards_view.title') }}</h1>
      <div class="search-options">
        <h-select hide-details dense label="" :items="regionDisplayOptionsI18n" class="region-selector" color="secondary" v-model="selectedRegion"></h-select>
        <h-search-field
          class="search-text"
          :placeholder="$t('components.home_gift_cards_view.search_box_hint')"
          hide-details
          color="secondary"
          :loading="working"
          v-model="storeTextFilter"
          clearable
          @keydown="onKeydown"
          @click:clear="loadData(true)"
        ></h-search-field>
        <div class="search-flags">
          <div class="promotion-select" style="margin-bottom: 0px">
            <h-checkbox color="secondary" :label="$t('components.home_gift_cards_view.promotion_label')" v-model="promotion_only"></h-checkbox>
          </div>
          <div class="promotion-sold-out-select">
            <h-checkbox color="secondary" :label="$t('components.home_gift_cards_view.promotion_sold_out_label')" v-model="promotion_sold_out"></h-checkbox>
          </div>
        </div>
      </div>
      <div ref="hashScrollAnchor" style="margin: 0px"></div>

      <v-progress-circular v-if="!dataReady" :style="{ marginTop: '50px', marginBottom: '50px', marginLeft: 'auto', marginRight: 'auto', width: '100%' }" :size="100" :width="7" indeterminate color="primary" />
      <div v-else-if="!offers.length">
        <div class="not-found-card">
          <div>
            <img src="@/assets/no_gift_card_found.png" style="max-width: 470px; width: 100%" />
          </div>
          <div>
            <p class="h-label-1" style="margin-bottom: var(--gap)">{{ $t('components.home_gift_cards_view.nothing_found') }}</p>
            <p class="h-text-2">{{ $t('components.home_gift_cards_view.nothing_found_sub') }}</p>
          </div>
          <div>
            <h-btn rounded text color="secondary" outlined @click="resetFilterLoad" large :loading="working">{{ $t('components.home_gift_cards_view.reset_filter_btn') }}</h-btn>
          </div>
        </div>
      </div>
      <div v-else class="grid-small-card">
        <marketing-offer-card v-for="(offer, index) in offers" :offer="offer" :key="index" />
      </div>
      <div style="text-align: center" v-if="canLoadMore">
        <h-btn rounded text color="secondary" outlined @click="loadMore" large :loading="working"> {{ $t('components.home_gift_cards_view.more_btn') }}</h-btn>
      </div>

      <recently-visited-space-widget style="margin-top: 72px" :enable-discover="false" />
    </div>
  </div>
</template>

<script>
import RecentlyVisitedSpaceWidget from '@/components/space/RecentlyVisitedSpaceWidget.vue';
import MarketingOfferCard from '@/components/marketing/MarketingOfferCard.vue';
import safeExecute from '@/composables/safe-execute';
import { mapGetters, mapActions } from 'vuex';
import AccountEmailPendingVerification from '@/components/account/AccountEmailPendingVerification.vue';
import AccountCardRequired from '@/components/account/AccountCardRequired.vue';

export default {
  name: 'home-gift-cards-view',
  title: (args) => args.$t('page_title.home_gift_cards'),
  setup(props, { root }) {
    const { execute, working } = safeExecute(root.$store);

    return {
      execute,
      working,
    };
  },
  watch: {
    currentLocale() {
      this.$title = this.$t('page_title.home_gift_cards');
    },
  },
  async created() {
    if (this.$route?.query?.s) {
      this.storeTextFilter = this.$route.query.s;
    }
    if (this.$route?.query?.region) {
      this.selectedRegion = this.$route.query.region;
    }
    if (this.$route?.query?.promo) {
      this.promotion_only = this.$route.query.promo;
    }
    if (this.$route?.query?.soldout) {
      this.promotion_sold_out = this.$route.query.soldout;
    }

    if (this.dataIsAlreadyLoaded) {
      this.dataReady = true;
    } else {
      await this.loadData();
    }
  },

  components: {
    MarketingOfferCard,
    RecentlyVisitedSpaceWidget,
    AccountEmailPendingVerification,
    AccountCardRequired,
  },
  data() {
    return {
      timerDelayRef: null,
      dataReady: false,
    };
  },
  computed: {
    ...mapGetters('giftCertificate', ['dataIsAlreadyLoaded', 'currentRegion', 'regionOptions', 'offers', 'promotionOnly', 'promotionSoldOut', 'searchKeyword', 'canLoadMore']),
    ...mapGetters('ui', ['currentLocale', 'displayAccountCardRequiredMessage', 'displayAccountEmailPendingVerificationMessage']),
    ...mapGetters('account', ['canRequestCard', 'emailNeedVerification']),
    ...mapGetters('security', ['authenticated']),
    cardApplyRedirectLink() {
      return {
        url: window.location.href,
        label_fr: this.$t('components.card_application.redirect_link.return_to_home_label', 'fr'),
        label_en: this.$t('components.card_application.redirect_link.return_to_home_label', 'en'),
      };
    },

    regionDisplayOptionsI18n() {
      return this.regionOptions.map((region) => {
        return {
          text: this.$t(`components.region_selection.regions.${region}`),
          value: region,
        };
      });
    },
    promotion_sold_out: {
      get() {
        return this.promotionSoldOut;
      },
      set(newValue) {
        if (newValue === true || newValue === false) {
          this.updatePromotionSoldOut(newValue);
        } else if (newValue === 'true') {
          this.updatePromotionSoldOut(true);
        } else if (newValue === 'false') {
          this.updatePromotionSoldOut(false);
        }
        this.loadData(true);
      },
    },
    promotion_only: {
      get() {
        return this.promotionOnly;
      },
      set(newValue) {
        if (newValue === true || newValue === false) {
          this.updatePromotionOnly(newValue);
        } else if (newValue === 'true') {
          this.updatePromotionOnly(true);
        } else if (newValue === 'false') {
          this.updatePromotionOnly(false);
        }
        this.loadData(true);
      },
    },
    selectedRegion: {
      get() {
        return this.currentRegion;
      },
      set(newValue) {
        this.updateRegion(newValue);
        this.loadData(true);
      },
    },
    storeTextFilter: {
      get() {
        return this.searchKeyword;
      },
      set(newValue) {
        this.updateSearchKeyword(newValue);
      },
    },
  },
  async mounted() {
    await this.listPublicSpaces();
  },
  methods: {
    ...mapActions('space', ['listPublicSpaces']),
    ...mapActions('giftCertificate', ['updateRegion', 'updatePromotionOnly', 'updateSearchKeyword', 'updatePromotionSoldOut']),
    onKeydown() {
      clearTimeout(this.timerDelayRef);
      this.timerDelayRef = setTimeout(() => {
        this.loadData(true);
      }, 150);
    },
    resetFilterLoad() {
      this.updatePromotionOnly(false);
      this.updatePromotionSoldOut(false);
      this.updateSearchKeyword(undefined);
      this.loadData(true);
    },
    async loadData(reset = false) {
      await this.execute(
        {
          action: 'giftCertificate/searchGiftCertificate',
          success: () => {
            this.dataReady = true;
          },
        },
        { locale: this.currentLocale, reset },
      );
    },
    async loadMore() {
      await this.execute({
        action: 'giftCertificate/nextPage',
      });
      await this.loadData();
    },

    backHome() {
      this.$router.pushNoDuplicate({ name: 'home' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/utilities/mixin.scss';

.home-gift-cards-view-content {
  --home-card-font-size: 24px;
  --home-card-region-font-size: 12px;
  --home-card-badge: 36px;

  max-width: var(--page-content-max-width);
  margin: 0 auto;
  padding: var(--gap) var(--gap) 0 var(--gap);
  @media ($sm-min) {
    --home-card-font-size: 32px;
    --home-card-region-font-size: 14px;
    --home-card-badge: 42px;
    padding: var(--gap-xxlarge) var(--gap) 0 var(--gap);
  }

  & > *:not(:last-child) {
    margin-bottom: var(--gap-large);
    @media ($sm-min) {
      margin-bottom: var(--gap-xxlarge);
    }
  }
}

.grid-small-card {
  display: grid;
  grid-gap: var(--gap);
  grid-template-columns: repeat(auto-fill, minmax(312px, 1fr));
  grid-template-rows: 1fr;
  @media (min-width: 1800px) {
    grid-template-columns: repeat(auto-fill, minmax(312px, 1fr));
  }
  margin-bottom: 48px;
}

.region-selector {
  padding-top: 0 !important;
  grid-area: regionSelect;
  & ::v-deep .mdi {
    color: black;
  }
}
.search-text {
  grid-area: filterText;
  padding-top: 0 !important;
  & ::v-deep .mdi {
    color: black;
  }
}

.search-text ::v-deep.v-progress-linear {
  max-width: calc(100% - 50px);
  left: 25px;
  height: 4px;
}

.search-text ::v-deep.v-input__slot {
  border: 1px solid var(--color-text-primary);
  @media ($sm-min) {
    border-radius: 40px;
    height: 56px;
  }
}
.region-selector ::v-deep.v-input__slot {
  border-radius: 25px;
  border: 1px solid var(--color-text-primary);
  @media ($sm-min) {
    border-radius: 40px;
    height: 56px;
  }
}

::v-deep .v-label {
  color: var(--color-text-primary);
}

.search-options {
  padding: var(--gap) 0;
  & > *:not(:last-child) {
    margin-bottom: 12px;
    @media ($lg-min) {
      margin-bottom: 0;
    }
  }
  @media ($md-min) {
    padding: 0px;
    display: grid;
    grid-template-columns: 1fr 48px 1fr;
    grid-template-areas:
      'regionSelect . filterText'
      'flags flags flags';
  }
  @media ($lg-min) {
    padding: 0px;
    display: grid;
    grid-template-columns: [regionSelect] 200px [flags] 1fr [filterText] 400px;
    grid-template-areas: 'regionSelect flags filterText';
  }
}
.search-flags {
  grid-area: flags;
  display: block;

  @media ($md-min) {
    display: flex;
  }
}

.promotion-select {
  padding: 12px 9px;
  grid-area: promoCheck;
  align-content: center;
  @media ($md-min) {
    padding: 12px;
  }
}

.promotion-sold-out-select {
  padding: 12px 9px;
  grid-area: promoSoldOutCheck;
  align-content: center;
  @media ($md-min) {
    padding: 12px;
  }
}

.title-header {
  color: var(--color-secondary);
  font-family: 'Apfel Grotezk';
  font-size: 40px;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
  @media ($md-min) {
    font-size: 70px;
  }
}

.v-btn.back-btn {
  min-width: 0 !important;
  height: 60px;
  padding: 0;
  @media ($sm-min) {
    font-size: 16px;
    padding: 12px;
  }
}

.v-btn.secondary--text {
  border-color: black !important;
}

.not-found-card {
  border-radius: 20px;
  background-color: var(--color-ultra-light);
  text-align: center;
  box-shadow: 0px 8px 12px 0px rgba(106, 104, 173, 0.12);
  padding: 36px 26px;
  @media ($sm-min) {
    padding-top: 62px;
    padding-bottom: 47px;
  }

  & > *:not(:last-child) {
    margin-bottom: 36px;
  }
  margin-bottom: 112px;
}

.recently-visited-space-widget ::v-deep .desktop-widget {
  padding-bottom: var(--gap);
}
</style>
